import { Component, OnDestroy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, throwError } from 'rxjs';
import { catchError, finalize, takeUntil, tap } from 'rxjs/operators';
import { AuthService, NotificationService } from '../../../core/services';
import { SsoProviderId } from '../../../core/config/sso-providers';

/** Mirrors the server's own check password_confirmation must match password. Catching this
 *  client-side avoids a round trip for the single most common signup mistake. */
function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirmation = group.get('password_confirmation')?.value;
  return password && confirmation && password !== confirmation ? { passwordMismatch: true } : null;
}

@Component({
  selector: 'app-signup',
  standalone: false,
  templateUrl: './signup.html',
  styleUrl: './signup.scss',
})
export class Signup implements OnDestroy {
  private readonly _destroy$ = new Subject<void>();

  isLoading = false;

  form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router,
  ) {
    this.form = this.fb.group({
      first_name: ['', [Validators.required]],
      last_name: ['', [Validators.required]],
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(128)]],
      password_confirmation: ['', [Validators.required]],
      organization_name: ['', [Validators.maxLength(120)]],
    }, { validators: passwordsMatch });
  }

  get passwordMismatch(): boolean {
    const confirmation = this.form.get('password_confirmation');
    return !!this.form.errors?.['passwordMismatch'] && !!confirmation?.touched;
  }

  signInWithSso(provider: SsoProviderId): void {
    this.authService.startSso(provider);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { first_name, last_name, email, password, password_confirmation, organization_name } = this.form.value;

    this.isLoading = true;
    this.authService.signup({
      name: `${first_name} ${last_name}`.trim().slice(0, 120),
      email,
      password,
      passwordConfirmation: password_confirmation,
      organizationName: organization_name || undefined,
    })
      .pipe(
        takeUntil(this._destroy$),
        catchError(error => {
          const apiError = error?.error?.error;
          const message = apiError?.code === 'validation_failed' && apiError?.details
            ? Object.values(apiError.details).flat().join(' ')
            : 'An error occurred while creating your account. Please try again.';
          this.notificationService.error(message);
          return throwError(() => error);
        }),
        tap(() => {
          // No token comes back here the account needs email confirmation before it can sign in.
          // The check-email page carries the "what happens next" messaging, so no toast here too.
          this.router.navigate(['/check-email'], { queryParams: { email } });
        }),
        finalize(() => { this.isLoading = false; }),
      ).subscribe();
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }
}
